import React from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/joy';

const POLICY_PDF = '/documents/Keystone_Subcontractor_Fleet_Driver_Policy_v1.2.pdf';
const ACCIDENT_LINE = '023 8212 0113';
const OFFICE_LINE = '01243 302841';

type Section = { title: string; points: string[] };

/** Keystone Subcontractor Fleet Driver Policy v1.2 (21 October 2026), as issued in the PDF. */
const SECTIONS: Section[] = [
  {
    title: '1. Authorisation and driver eligibility',
    points: [
      'No one may drive a fleet vehicle without prior express permission from Keystone and verification of appropriate insurance cover. No lending, sharing or unapproved drivers.',
      'Drivers must be aged 30 or over. Drivers aged 29 or under are not authorised to drive Keystone fleet vehicles.',
      'Before handover, provide full name, date of birth, driving-licence details and evidence of entitlement to drive the vehicle; permit periodic DVLA licence checks with an appropriate check code.',
      'Drivers must hold a full UK driving licence for at least two years. The standard fleet driver criteria require no motoring convictions in the preceding five years, no driving bans and no DVLA-notifiable medical conditions. Any driver outside these criteria must receive prior insurer approval, confirmed by Keystone, before driving.',
      'Declare all motoring convictions and penalty points before being authorised to drive, including any pending prosecutions. Immediately inform Keystone of new convictions, penalty points, driving bans, changes in licence entitlement or DVLA-reportable medical conditions.',
    ],
  },
  {
    title: '2. Permitted vehicle use',
    points: [
      'Keystone fleet vehicles may be used only for authorised Keystone business journeys and commuting between home and the normal place of work. Social, domestic, pleasure and other personal journeys are not permitted.',
      'Vehicles may carry Keystone tools, materials and goods for authorised work. Do not use them for paid courier or delivery services, carrying passengers or goods for hire or reward, other businesses’ work, racing or competitions.',
      'Weekend vehicle use is prohibited unless express permission has been obtained from Keystone in advance. Weekend use requires an ad-hoc insurance adjustment arranged by Keystone; permission must be confirmed before the vehicle is used. No unauthorised overseas use, towing or hazardous loads.',
      'Observe all applicable road traffic laws, licence restrictions, speed limits, load/payload limits, safe loading and seatbelt requirements. Never drive while impaired by alcohol, drugs, fatigue or distraction.',
    ],
  },
  {
    title: '3. Vehicle care and security',
    points: [
      'Perform a basic pre-use walkaround: tyres, lights, fluids, visible damage, windscreen, warning lights, number plates and load security. Report any issue promptly and do not drive an unsafe or unroadworthy vehicle.',
      'Keep vans clean and protect the interior, tools and fittings. Report damage, loss, theft, faults and warning lights without delay. No alterations, equipment installations or software changes without authorisation.',
      'Whenever leaving the vehicle unattended, turn off the engine, remove the keys, lock all doors and secure valuables. Theft cover may be excluded if keys are left in or on the vehicle, or if the engine is left running while unattended.',
      'Park responsibly in a secure location where practicable. Advise Keystone in advance of any change to the vehicle’s usual overnight parking location.',
      'Use only Keystone-authorised garages and servicing providers. Breakdown cover is not included in the fleet insurance; in the event of a breakdown, contact Keystone for instructions and do not arrange recovery without approval unless necessary for immediate safety.',
    ],
  },
  {
    title: '4. Accident, theft and incident procedure',
    points: [
      'Stop safely, switch off the engine and assist anyone injured. Call 999 where required. Exchange legally required details and photograph vehicles, scene and damage where safe. Record witnesses and other parties’ details.',
      `Notify Keystone immediately, however minor the collision or damage. Contact Business Compare’s dedicated motor accident line: ${ACCIDENT_LINE}. Follow the insurer/claims handler’s instructions for recovery and approved repairs.`,
      'Do not admit liability, agree to pay, settle a claim or arrange repairs independently. Forward any letters, demands or police/prosecution correspondence to Keystone immediately.',
      'For theft, take reasonable steps to contact the police, obtain an incident/crime reference and notify Keystone/claims handlers without delay.',
    ],
  },
  {
    title: '5. Costs, excesses and responsibility',
    points: [
      'The fleet insurance currently specifies a £250 excess for accidental damage, a £250 excess for fire or theft and a £150 excess for glass replacement. Drivers must report any incident immediately. Any responsibility for excesses, damage or other costs will be determined under the separate subcontractor agreement or another written agreement; this policy does not itself authorise deductions.',
    ],
  },
  {
    title: '6. Handover, return and acknowledgement',
    points: [
      'Return the vehicle, keys, fuel card and all supplied property immediately upon request or cessation of authorisation. Report all new damage and outstanding faults.',
      'Keystone may withdraw vehicle permission for unsafe driving, misuse, non-disclosure or breach of this policy.',
    ],
  },
];

const telHref = (number: string) => `tel:${number.replace(/\s+/g, '')}`;

/**
 * Fitters and labourers: the fleet driver rules they sign at van handover, readable on a phone,
 * with the accident line one tap away.
 */
const FleetDriverPolicyPage: React.FC = () => (
  <Box sx={{ px: { xs: 0.5, sm: 1 }, py: { xs: 1, md: 2 }, maxWidth: 720, mx: 'auto', width: '100%' }}>
    <Box sx={{ mb: 2 }}>
      <Typography level="h3" sx={{ mb: 0.35, fontSize: { xs: '1.45rem', sm: '1.75rem' } }}>
        Fleet Driver Policy
      </Typography>
      <Typography level="body-sm" sx={{ color: 'neutral.500', fontWeight: 700 }}>
        Driver rules and vehicle handover agreement · Version 1.2 · 21 October 2026
      </Typography>
    </Box>

    <Alert color="danger" variant="soft" sx={{ mb: 2, alignItems: 'flex-start' }}>
      <Stack gap={1} sx={{ width: '100%' }}>
        <Typography level="title-sm" sx={{ color: 'inherit' }}>Accident or theft?</Typography>
        <Typography level="body-sm" sx={{ color: 'inherit' }}>
          Make the scene safe and call 999 if anyone is hurt. Then tell Keystone and the motor accident line straight away, however minor. Don&apos;t admit liability.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
          <Button component="a" href={telHref(ACCIDENT_LINE)} color="danger" size="sm">
            Accident line {ACCIDENT_LINE}
          </Button>
          <Button component="a" href={telHref(OFFICE_LINE)} color="neutral" variant="outlined" size="sm">
            Keystone {OFFICE_LINE}
          </Button>
        </Stack>
      </Stack>
    </Alert>

    <Stack gap={1.5}>
      {SECTIONS.map((section) => (
        <Card key={section.title} variant="outlined">
          <CardContent sx={{ py: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 2 } }}>
            <Typography level="title-md" sx={{ mb: 1 }}>{section.title}</Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.25, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {section.points.map((point) => (
                <Typography key={point} component="li" level="body-sm">{point}</Typography>
              ))}
            </Box>
          </CardContent>
        </Card>
      ))}
    </Stack>

    <Typography level="body-xs" sx={{ color: 'neutral.500', mt: 2 }}>
      You sign this policy, with your vehicle registration and licence check date, when the vehicle is handed over.
    </Typography>
    <Button component="a" href={POLICY_PDF} target="_blank" rel="noopener" variant="outlined" color="neutral" size="sm" sx={{ mt: 1.5 }}>
      Download PDF
    </Button>
  </Box>
);

export default FleetDriverPolicyPage;
